import React, { useState, useEffect, useCallback } from 'react';
import { useWebSocket } from '../api/websocketService';
import { processProjectAssets, processProjectWithProfiles, getProjectStatus, cancelProjectProcessing, getPriorityQueueStatus, getProcessingOverview, getPriorityLevels } from '../api/assetService';
import { profileService, Profile } from '../api/profileService';

// Simple UI components since the ./ui module doesn't exist
const Button: React.FC<{
  children: React.ReactNode;
  onClick?: () => void;
  type?: 'button' | 'submit';
  disabled?: boolean;
  variant?: 'default' | 'outline' | 'destructive' | 'secondary';
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}> = ({ children, onClick, type = 'button', disabled, variant = 'default', size = 'md', className = '' }) => {
  const baseClasses = 'px-4 py-2 rounded font-medium transition-colors';
  const variantClasses = {
    default: 'bg-blue-500 text-white hover:bg-blue-600',
    outline: 'border border-gray-300 text-gray-700 hover:bg-gray-50',
    destructive: 'bg-red-500 text-white hover:bg-red-600',
    secondary: 'bg-gray-500 text-white hover:bg-gray-600'
  };
  const sizeClasses = {
    sm: 'px-2 py-1 text-sm',
    md: 'px-4 py-2',
    lg: 'px-6 py-3 text-lg'
  };
  
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={`${baseClasses} ${variantClasses[variant]} ${sizeClasses[size]} ${className} ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
    >
      {children}
    </button>
  );
};

const Input: React.FC<{
  id?: string;
  type?: string;
  value?: string | number;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  min?: string | number;
  max?: string | number;
  className?: string;
}> = ({ id, type = 'text', value, onChange, placeholder, disabled, required, min, max, className = '' }) => {
  return (
    <input
      id={id}
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      disabled={disabled}
      required={required}
      min={min}
      max={max}
      className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${className}`}
    />
  );
};

const Label: React.FC<{
  htmlFor?: string;
  children: React.ReactNode;
  className?: string;
}> = ({ htmlFor, children, className = '' }) => {
  return (
    <label htmlFor={htmlFor} className={`block text-sm font-medium text-gray-700 mb-1 ${className}`}>
      {children}
    </label>
  );
};

const Select: React.FC<{
  id?: string;
  value?: string | number;
  onChange?: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  disabled?: boolean;
  children: React.ReactNode;
  className?: string;
}> = ({ id, value, onChange, disabled, children, className = '' }) => {
  return (
    <select
      id={id}
      value={value}
      onChange={onChange}
      disabled={disabled}
      className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${className}`}
    >
      {children}
    </select>
  );
};

const Card: React.FC<{
  children: React.ReactNode;
  className?: string;
}> = ({ children, className = '' }) => {
  return (
    <div className={`bg-white shadow rounded-lg ${className}`}>
      {children}
    </div>
  );
};

const Badge: React.FC<{
  children: React.ReactNode;
  variant?: 'default' | 'outline' | 'secondary';
  className?: string;
}> = ({ children, variant = 'default', className = '' }) => {
  const variantClasses = {
    default: 'bg-blue-100 text-blue-800',
    outline: 'border border-gray-300 text-gray-700',
    secondary: 'bg-gray-100 text-gray-800'
  };
  
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${variantClasses[variant]} ${className}`}>
      {children}
    </span>
  );
};

const Progress: React.FC<{
  value: number;
  className?: string;
}> = ({ value, className = '' }) => {
  return (
    <div className={`w-full bg-gray-200 rounded-full h-2 ${className}`}>
      <div
        className="bg-blue-600 h-2 rounded-full transition-all duration-300"
        style={{ width: `${Math.min(100, Math.max(0, value))}%` }}
      />
    </div>
  );
};

const Alert: React.FC<{
  children: React.ReactNode;
  variant?: 'default' | 'destructive';
  className?: string;
}> = ({ children, variant = 'default', className = '' }) => {
  const variantClasses = {
    default: 'bg-green-50 border-green-200 text-green-800',
    destructive: 'bg-red-50 border-red-200 text-red-800'
  };
  
  return (
    <div className={`border rounded-md p-4 ${variantClasses[variant]} ${className}`}>
      {children}
    </div>
  );
};

const Spinner: React.FC<{
  className?: string;
}> = ({ className = '' }) => {
  return (
    <svg
      className={`animate-spin h-4 w-4 ${className}`}
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );
};

const SYSTEM_DEFAULT_PROMPT_PROFILE_ID = 'default';
const USE_DEFAULT_PROMPT_OPTION = '__use_default_prompt__';
const DEFAULT_PROMPT_LABEL = 'Use default prompt profile (system default)';

const normalizeStoredProfileId = (value: string | null): string | null => {
  if (!value) {
    return null;
  }
  if (
    value === SYSTEM_DEFAULT_PROMPT_PROFILE_ID ||
    value === USE_DEFAULT_PROMPT_OPTION
  ) {
    return USE_DEFAULT_PROMPT_OPTION;
  }
  return value;
};

const toStorageProfileId = (value: string): string => {
  return value === USE_DEFAULT_PROMPT_OPTION
    ? SYSTEM_DEFAULT_PROMPT_PROFILE_ID
    : value;
};

const resolveProfileSelectValue = (profile: Profile): string => {
  const profileId = profile.profile_id || profile.id;
  if (profileId === SYSTEM_DEFAULT_PROMPT_PROFILE_ID) {
    return USE_DEFAULT_PROMPT_OPTION;
  }
  return profileId;
};

interface BatchRequest {
  projectId: string;
  batchSize?: number;
  priority: number;
  metadata?: {
    user_id?: string;
    source?: string;
    description?: string;
  };
}

interface ProcessingStatus {
  project_id: string;
  is_processing: boolean;
  is_queued: boolean;
  queue_position?: number;
  priority?: number;
  priority_label?: string;
  estimated_start_time?: string;
  task_status: string;
  total_assets: number;
  processed_assets: number;
  failed_assets: number;
  in_progress_assets: number;
  pending_assets: number;
  status_summary: {
    pending: number;
    processing: number;
    completed: number;
    failed: number;
  };
}

interface QueueItem {
  position: number;
  project_id: string;
  priority: number;
  priority_label: string;
  wait_time: number;
  estimated_assets: number;
  user_id?: string;
}

interface ProcessingOverview {
  active_projects: Array<{
    project_id: string;
    status: string;
    task_status: string;
    priority: number;
    priority_label: string;
  }>;
  total_active_projects: number;
  max_concurrent_projects: number;
  available_slots: number;
  queue_size: number;
  queue_statistics: Record<string, any>;
  active_priorities: number[];
  metrics: {
    total_projects_processed: number;
    total_assets_processed: number;
    average_processing_time: number;
    success_rate: number;
  };
}

interface PriorityLevel {
  value: number;
  label: string;
  description: string;
  resource_multiplier: number;
  time_multiplier: number;
}

const EnhancedAssetProcessor: React.FC = () => {
  const [batchRequest, setBatchRequest] = useState<BatchRequest>({
    projectId: '',
    batchSize: 50,
    priority: 1,
    metadata: {
      source: 'frontend',
      description: ''
    }
  });

  const [enableAiProcessing, setEnableAiProcessing] = useState(true);
  const [selectedProfileId, setSelectedProfileId] = useState<string>('');
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [loadingProfiles, setLoadingProfiles] = useState(false);

  interface ProcessingState {
    isProcessing: boolean;
    currentProject: string;
    error: string;
    success: string;
  }

  const [processingState, setProcessingState] = useState<ProcessingState>({
    isProcessing: false,
    currentProject: '',
    error: '',
    success: ''
  });

  const [projectStatuses, setProjectStatuses] = useState<Record<string, ProcessingStatus>>({});
  const [queueItems, setQueueItems] = useState<QueueItem[]>([]);
  const [processingOverview, setProcessingOverview] = useState<ProcessingOverview | null>(null);
  const [priorityLevels, setPriorityLevels] = useState<PriorityLevel[]>([]);
  const [activeTab, setActiveTab] = useState<'processing' | 'queue' | 'overview'>('processing');
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [refreshInterval, setRefreshInterval] = useState(5000);

  const defaultProfileInfo = profiles.find(
    (profile) =>
      (profile.profile_id || profile.id) === SYSTEM_DEFAULT_PROMPT_PROFILE_ID
  );
  const defaultProfileLabel = defaultProfileInfo
    ? `Use default prompt profile (${defaultProfileInfo.name})`
    : DEFAULT_PROMPT_LABEL;
  const otherProfiles = profiles.filter(
    (profile) =>
      (profile.profile_id || profile.id) !== SYSTEM_DEFAULT_PROMPT_PROFILE_ID
  );

  // Mock WebSocket hook since the actual one doesn't exist
  const { socket, isConnected } = {
    socket: null,
    isConnected: false
  };

  // Priority level options
  const priorityOptions = [
    { value: 1, label: 'Low', color: 'bg-gray-100 text-gray-800' },
    { value: 2, label: 'Medium', color: 'bg-blue-100 text-blue-800' },
    { value: 3, label: 'High', color: 'bg-orange-100 text-orange-800' },
    { value: 4, label: 'Critical', color: 'bg-red-100 text-red-800' }
  ];

  // Load priority levels on component mount
  useEffect(() => {
    const loadPriorityLevels = async () => {
      try {
        const data = await getPriorityLevels();
        setPriorityLevels(data.priority_levels);
      } catch (error) {
        console.error('Failed to load priority levels:', error);
      }
    };

    loadPriorityLevels();
  }, []);

  // Auto-refresh functionality
  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      if (processingState.currentProject) {
        fetchProjectStatus(processingState.currentProject);
      }
      fetchQueueStatus();
      fetchProcessingOverview();
    }, refreshInterval);

    return () => clearInterval(interval);
  }, [autoRefresh, refreshInterval, processingState.currentProject]);

  // WebSocket event handling
  useEffect(() => {
    if (!socket) return;

    const handleProcessingUpdate = (data: any) => {
      if (data.project_id && projectStatuses[data.project_id]) {
        fetchProjectStatus(data.project_id);
      }
    };

    const handleQueueUpdate = () => {
      fetchQueueStatus();
      fetchProcessingOverview();
    };

    // Mock WebSocket events since socket doesn't exist
    // socket.on('processing_update', handleProcessingUpdate);
    // socket.on('queue_update', handleQueueUpdate);

    return () => {
      // socket.off('processing_update', handleProcessingUpdate);
      // socket.off('queue_update', handleQueueUpdate);
    };
  }, [socket, projectStatuses]);

  const fetchProjectStatus = useCallback(async (projectId: string) => {
    try {
      const status = await getProjectStatus(projectId);
      setProjectStatuses(prev => ({
        ...prev,
        [projectId]: status
      }));
    } catch (error) {
      console.error(`Failed to fetch status for project ${projectId}:`, error);
    }
  }, []);

  const fetchQueueStatus = useCallback(async () => {
    try {
      const queueStatus = await getPriorityQueueStatus();
      setQueueItems(queueStatus.queue_items || []);
    } catch (error) {
      console.error('Failed to fetch queue status:', error);
    }
  }, []);

  const fetchProcessingOverview = useCallback(async () => {
    try {
      const overview = await getProcessingOverview();
      setProcessingOverview(overview);
    } catch (error) {
      console.error('Failed to fetch processing overview:', error);
    }
  }, []);

  // Load profiles when AI processing is enabled
  const loadProfiles = useCallback(async () => {
    if (!enableAiProcessing) {
      setProfiles([]);
      setSelectedProfileId('');
      return;
    }
    
    try {
      setLoadingProfiles(true);
      // Use the same endpoint as Bulk Indexing for consistency
      // The /api/prompts/profiles endpoint returns all available profiles
      // Profiles are unified and access-controlled via allowed_project_ids in metadata
      const fetchedProfiles = await profileService.getProfiles();
      
      setProfiles(fetchedProfiles);
      
      // Set default profile if available and none selected
      if (fetchedProfiles.length > 0 && !selectedProfileId) {
        const lastSelectedProfileId = localStorage.getItem('enhanced_processing_last_profile_id');
        const normalizedStoredProfileId =
          normalizeStoredProfileId(lastSelectedProfileId);
        const lastSelectedProfile = lastSelectedProfileId
          ? fetchedProfiles.find(
              (p) =>
                (p.profile_id === lastSelectedProfileId) ||
                (p.id === lastSelectedProfileId)
            )
          : null;

        const defaultProfile =
          lastSelectedProfile ||
          fetchedProfiles.find((p) => p.is_default) ||
          fetchedProfiles.find(
            (p) =>
              p.profile_id === SYSTEM_DEFAULT_PROMPT_PROFILE_ID ||
              p.id === SYSTEM_DEFAULT_PROMPT_PROFILE_ID
          ) ||
          fetchedProfiles.find((p) =>
            p.name?.toLowerCase().includes('default')
          ) ||
          fetchedProfiles[0];

        const resolvedProfileId =
          normalizedStoredProfileId ||
          (defaultProfile ? resolveProfileSelectValue(defaultProfile) : '');

        if (resolvedProfileId) {
          setSelectedProfileId(resolvedProfileId);
          localStorage.setItem(
            'enhanced_processing_last_profile_id',
            toStorageProfileId(resolvedProfileId)
          );
        }
      }
    } catch (error) {
      console.error('Error loading profiles:', error);
      // Don't show alert - just log, profiles are optional
      setProfiles([
        { id: 'default', name: 'Default Profile', description: 'Default AI processing profile' },
      ]);
      if (!selectedProfileId) {
        setSelectedProfileId(USE_DEFAULT_PROMPT_OPTION);
        localStorage.setItem(
          'enhanced_processing_last_profile_id',
          SYSTEM_DEFAULT_PROMPT_PROFILE_ID
        );
      }
    } finally {
      setLoadingProfiles(false);
    }
  }, [enableAiProcessing, selectedProfileId]);

  // Load profiles when AI processing is enabled or project ID changes
  useEffect(() => {
    if (enableAiProcessing) {
      loadProfiles();
    } else {
      // Clear selection when AI processing is disabled
      setSelectedProfileId('');
      setProfiles([]);
    }
  }, [enableAiProcessing, batchRequest.projectId, loadProfiles]);

  // Normalize selected profile ID once real profile metadata has been loaded
  useEffect(() => {
    if (!profiles.length || !selectedProfileId || selectedProfileId === USE_DEFAULT_PROMPT_OPTION) {
      return;
    }

    const matchedProfile = profiles.find((profile) =>
      [
        profile.profile_id,
        profile.id,
        profile.name,
      ].includes(selectedProfileId)
    );

    if (matchedProfile && matchedProfile.profile_id && selectedProfileId !== matchedProfile.profile_id) {
      setSelectedProfileId(matchedProfile.profile_id);
    }
  }, [profiles, selectedProfileId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!batchRequest.projectId.trim()) {
      setProcessingState((prev: ProcessingState) => ({ ...prev, error: 'Project ID is required' }));
      return;
    }

    setProcessingState({
      isProcessing: true,
      currentProject: batchRequest.projectId,
      error: '',
      success: ''
    });

    try {
      let response: any;
      
      // Use processProjectWithProfiles if AI processing is enabled and profile is selected
      if (enableAiProcessing && selectedProfileId) {
        const isDefaultOptionSelected =
          selectedProfileId === USE_DEFAULT_PROMPT_OPTION;
        const selectedProfile = !isDefaultOptionSelected
          ? profiles.find(
              (profile) =>
                profile.profile_id === selectedProfileId ||
                profile.id === selectedProfileId ||
                profile.name === selectedProfileId
            )
          : undefined;
        const profileIdToSend = isDefaultOptionSelected
          ? SYSTEM_DEFAULT_PROMPT_PROFILE_ID
          : selectedProfile?.profile_id || selectedProfileId;
        const profileNameToSend = isDefaultOptionSelected
          ? defaultProfileInfo?.name || 'Default profile'
          : selectedProfile?.name;
        response = await processProjectWithProfiles(batchRequest.projectId, {
          batch_size: batchRequest.batchSize,
          priority: batchRequest.priority,
          profile_ids: [profileIdToSend], // Send as array for enhanced processing
          metadata: {
            ...batchRequest.metadata,
            enable_ai_processing: true,
            selected_profile_id: profileIdToSend,
            selected_profile_name: profileNameToSend,
          },
        });
      } else {
        // Use regular processing without AI
        response = await processProjectAssets(batchRequest.projectId, {
          batch_size: batchRequest.batchSize,
          priority: batchRequest.priority,
          metadata: {
            ...batchRequest.metadata,
            enable_ai_processing: false
          }
        });
      }

      const priorityLabel = response.priority_label || priorityOptions.find(opt => opt.value === batchRequest.priority)?.label || 'Unknown';

      setProcessingState((prev: ProcessingState) => ({
        ...prev,
        success: `Processing started for project ${batchRequest.projectId} with ${priorityLabel} priority`,
        isProcessing: false
      }));

      // Fetch initial status
      await fetchProjectStatus(batchRequest.projectId);
      await fetchQueueStatus();
      await fetchProcessingOverview();

      // Reset form
      setBatchRequest(prev => ({
        ...prev,
        projectId: '',
        metadata: { ...prev.metadata, description: '' }
      }));

    } catch (error: any) {
      setProcessingState((prev: ProcessingState) => ({
        ...prev,
        error: error.message || 'Failed to start processing',
        isProcessing: false
      }));
    }
  };

  const handleCancel = async (projectId: string) => {
    try {
      await cancelProjectProcessing(projectId);
      setProcessingState((prev: ProcessingState) => ({
        ...prev,
        success: `Processing cancelled for project ${projectId}`
      }));
      
      // Refresh statuses
      await fetchProjectStatus(projectId);
      await fetchQueueStatus();
      await fetchProcessingOverview();
    } catch (error: any) {
      setProcessingState((prev: ProcessingState) => ({
        ...prev,
        error: error.message || 'Failed to cancel processing'
      }));
    }
  };

  const getPriorityBadge = (priority: number) => {
    const option = priorityOptions.find(opt => opt.value === priority);
    return (
      <Badge className={option?.color || 'bg-gray-100 text-gray-800'}>
        {option?.label || 'Unknown'}
      </Badge>
    );
  };

  const formatWaitTime = (seconds: number) => {
    if (seconds < 60) return `${Math.round(seconds)}s`;
    if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
    return `${Math.round(seconds / 3600)}h`;
  };

  const formatEstimatedTime = (isoString: string) => {
    try {
      const date = new Date(isoString);
      return date.toLocaleTimeString();
    } catch {
      return 'Unknown';
    }
  };

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900">Enhanced Asset Processor</h1>
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="autoRefresh"
              checked={autoRefresh}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAutoRefresh(e.target.checked)}
              className="rounded"
            />
            <label htmlFor="autoRefresh" className="text-sm text-gray-600">
              Auto-refresh
            </label>
          </div>
          <Select
            value={refreshInterval}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setRefreshInterval(Number(e.target.value))}
            className="w-32"
            disabled={!autoRefresh}
          >
            <option value={2000}>2s</option>
            <option value={5000}>5s</option>
            <option value={10000}>10s</option>
            <option value={30000}>30s</option>
          </Select>
        </div>
      </div>

      {/* Connection Status */}
      <div className="flex items-center space-x-2">
        <div className={`w-3 h-3 rounded-full ${isConnected ? 'bg-green-500' : 'bg-red-500'}`} />
        <span className="text-sm text-gray-600">
          {isConnected ? 'Connected to WebSocket' : 'Disconnected from WebSocket'}
        </span>
      </div>

      {/* Alerts */}
      {processingState.error && (
        <Alert variant="destructive">
          <span>{processingState.error}</span>
        </Alert>
      )}
      {processingState.success && (
        <Alert>
          <span>{processingState.success}</span>
        </Alert>
      )}

      {/* Processing Form */}
      <Card className="p-6">
        <h2 className="text-xl font-semibold mb-4">Start Batch Processing</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="projectId">Project ID</Label>
              <Input
                id="projectId"
                type="text"
                value={batchRequest.projectId}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setBatchRequest(prev => ({ ...prev, projectId: e.target.value }))}
                placeholder="Enter project ID"
                disabled={processingState.isProcessing}
                required
              />
            </div>
            
            <div>
              <Label htmlFor="batchSize">Batch Size</Label>
              <Input
                id="batchSize"
                type="number"
                value={batchRequest.batchSize || ''}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setBatchRequest(prev => ({ ...prev, batchSize: Number(e.target.value) }))}
                placeholder="50"
                min="1"
                max="200"
                disabled={processingState.isProcessing}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="priority">Priority</Label>
              <Select
                id="priority"
                value={batchRequest.priority}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setBatchRequest(prev => ({ ...prev, priority: Number(e.target.value) }))}
                disabled={processingState.isProcessing}
              >
                {priorityOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label} Priority
                  </option>
                ))}
              </Select>
              {priorityLevels.length > 0 && (
                <p className="text-xs text-gray-500 mt-1">
                  {priorityLevels.find(p => p.value === batchRequest.priority)?.description}
                </p>
              )}
            </div>

            <div>
              <Label htmlFor="description">Description (Optional)</Label>
              <Input
                id="description"
                type="text"
                value={batchRequest.metadata?.description || ''}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setBatchRequest(prev => ({
                  ...prev,
                  metadata: { ...prev.metadata, description: e.target.value }
                }))}
                placeholder="Processing description"
                disabled={processingState.isProcessing}
              />
            </div>
          </div>

          <div className="mt-4 p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center mb-3">
              <input
                type="checkbox"
                id="enableAiEnhanced"
                checked={enableAiProcessing}
                onChange={(e) => setEnableAiProcessing(e.target.checked)}
                disabled={processingState.isProcessing}
                className="mr-2"
              />
              <Label htmlFor="enableAiEnhanced" className="mb-0">
                Enable AI Processing
              </Label>
            </div>

            {enableAiProcessing && (
              <div className="mt-3">
                <Label htmlFor="profileSelectEnhanced">
                  AI Profile / Prompt
                  {loadingProfiles && <span className="text-xs text-gray-500 ml-2">(Loading...)</span>}
                </Label>
                <Select
                  id="profileSelectEnhanced"
                  value={selectedProfileId}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                    const profileId = e.target.value;
                    setSelectedProfileId(profileId);
                    if (profileId) {
                      localStorage.setItem(
                        'enhanced_processing_last_profile_id',
                        toStorageProfileId(profileId)
                      );
                    }
                  }}
                  disabled={loadingProfiles}
                >
                  <option value="">Select a profile...</option>
                  <option value={USE_DEFAULT_PROMPT_OPTION}>
                    {defaultProfileLabel}
                  </option>
                  {otherProfiles.map((profile) => {
                    const value = profile.profile_id || profile.id;
                    return (
                      <option key={value} value={value}>
                        {profile.name}
                        {profile.is_default ? ' (Default)' : ''}
                        {profile.description ? ` - ${profile.description}` : ''}
                      </option>
                    );
                  })}
                </Select>
                {!batchRequest.projectId && (
                  <p className="text-xs text-yellow-600 mt-1">
                    Note: Enter a project ID to load project-specific profiles.
                  </p>
                )}
                {!otherProfiles.length && !loadingProfiles && (
                  <p className="text-xs text-gray-500 mt-1">
                    Profiles will be loaded automatically, but you can use the default profile option above right now.
                  </p>
                )}
              </div>
            )}
          </div>

          <Button
            type="submit"
            disabled={processingState.isProcessing || !batchRequest.projectId.trim()}
            className="w-full md:w-auto"
          >
            {processingState.isProcessing ? (
              <>
                <Spinner className="mr-2" />
                Starting Processing...
              </>
            ) : (
              'Start Processing'
            )}
          </Button>
        </form>
      </Card>

      {/* Navigation Tabs */}
      <div className="flex space-x-1 border-b">
        <button
          onClick={() => setActiveTab('processing')}
          className={`px-4 py-2 rounded-t-lg ${
            activeTab === 'processing'
              ? 'bg-blue-500 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          Active Processing
        </button>
        <button
          onClick={() => setActiveTab('queue')}
          className={`px-4 py-2 rounded-t-lg ${
            activeTab === 'queue'
              ? 'bg-blue-500 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          Priority Queue
        </button>
        <button
          onClick={() => setActiveTab('overview')}
          className={`px-4 py-2 rounded-t-lg ${
            activeTab === 'overview'
              ? 'bg-blue-500 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          System Overview
        </button>
      </div>

      {/* Active Processing Tab */}
      {activeTab === 'processing' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">Active Processing</h3>
            <Button
              onClick={() => {
                Object.keys(projectStatuses).forEach(fetchProjectStatus);
                fetchQueueStatus();
                fetchProcessingOverview();
              }}
              variant="outline"
              size="sm"
            >
              Refresh
            </Button>
          </div>

          {Object.keys(projectStatuses).length === 0 ? (
            <Card className="p-6 text-center text-gray-500">
              No active processing projects
            </Card>
          ) : (
            <div className="grid gap-4">
              {Object.entries(projectStatuses).map(([projectId, status]) => (
                <Card key={projectId} className="p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h4 className="text-lg font-semibold">Project {projectId}</h4>
                      <div className="flex items-center space-x-2 mt-1">
                        {status.priority && getPriorityBadge(status.priority)}
                        <Badge variant={status.is_processing ? 'default' : status.is_queued ? 'secondary' : 'outline'}>
                          {status.task_status}
                        </Badge>
                      </div>
                    </div>
                    <Button
                      onClick={() => handleCancel(projectId)}
                      variant="destructive"
                      size="sm"
                      disabled={!status.is_processing && !status.is_queued}
                    >
                      Cancel
                    </Button>
                  </div>

                  {status.is_queued && (
                    <div className="mb-4 p-3 bg-blue-50 rounded-lg">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-blue-700">
                          Queue Position: {status.queue_position}
                        </span>
                        {status.estimated_start_time && (
                          <span className="text-sm text-blue-700">
                            Estimated Start: {formatEstimatedTime(status.estimated_start_time)}
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="space-y-3">
                    <div>
                      <div className="flex justify-between text-sm mb-1">
                        <span>Progress</span>
                        <span>
                          {status.processed_assets} / {status.total_assets} assets
                        </span>
                      </div>
                      <Progress
                        value={(status.processed_assets / status.total_assets) * 100}
                        className="h-2"
                      />
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <span className="text-gray-500">Total:</span>
                        <div className="font-semibold">{status.total_assets}</div>
                      </div>
                      <div>
                        <span className="text-gray-500">Completed:</span>
                        <div className="font-semibold text-green-600">{status.processed_assets}</div>
                      </div>
                      <div>
                        <span className="text-gray-500">Failed:</span>
                        <div className="font-semibold text-red-600">{status.failed_assets}</div>
                      </div>
                      <div>
                        <span className="text-gray-500">In Progress:</span>
                        <div className="font-semibold text-blue-600">{status.in_progress_assets}</div>
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Priority Queue Tab */}
      {activeTab === 'queue' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">Priority Queue</h3>
            <Button
              onClick={fetchQueueStatus}
              variant="outline"
              size="sm"
            >
              Refresh
            </Button>
          </div>

          {queueItems.length === 0 ? (
            <Card className="p-6 text-center text-gray-500">
              No projects in queue
            </Card>
          ) : (
            <div className="space-y-2">
              {queueItems.map((item) => (
                <Card key={`${item.project_id}-${item.position}`} className="p-4">
                  <div className="flex justify-between items-center">
                    <div className="flex items-center space-x-4">
                      <div className="text-2xl font-bold text-gray-400">#{item.position}</div>
                      <div>
                        <div className="font-semibold">Project {item.project_id}</div>
                        <div className="text-sm text-gray-500">
                          {item.estimated_assets} assets • Wait time: {formatWaitTime(item.wait_time)}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      {getPriorityBadge(item.priority)}
                      {item.user_id && (
                        <Badge variant="outline">User: {item.user_id}</Badge>
                      )}
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* System Overview Tab */}
      {activeTab === 'overview' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">System Overview</h3>
            <Button
              onClick={fetchProcessingOverview}
              variant="outline"
              size="sm"
            >
              Refresh
            </Button>
          </div>

          {processingOverview ? (
            <div className="grid gap-6">
              {/* System Status */}
              <Card className="p-6">
                <h4 className="text-lg font-semibold mb-4">System Status</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <div className="text-2xl font-bold text-blue-600">
                      {processingOverview.total_active_projects}
                    </div>
                    <div className="text-sm text-gray-500">Active Projects</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-green-600">
                      {processingOverview.available_slots}
                    </div>
                    <div className="text-sm text-gray-500">Available Slots</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-orange-600">
                      {processingOverview.queue_size}
                    </div>
                    <div className="text-sm text-gray-500">Queue Size</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-purple-600">
                      {Math.round((processingOverview.total_active_projects / processingOverview.max_concurrent_projects) * 100)}%
                    </div>
                    <div className="text-sm text-gray-500">Utilization</div>
                  </div>
                </div>
              </Card>

              {/* Performance Metrics */}
              <Card className="p-6">
                <h4 className="text-lg font-semibold mb-4">Performance Metrics</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <div className="text-2xl font-bold">
                      {processingOverview.metrics.total_projects_processed}
                    </div>
                    <div className="text-sm text-gray-500">Projects Processed</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold">
                      {processingOverview.metrics.total_assets_processed}
                    </div>
                    <div className="text-sm text-gray-500">Assets Processed</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold">
                      {Math.round(processingOverview.metrics.average_processing_time)}s
                    </div>
                    <div className="text-sm text-gray-500">Avg Processing Time</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold">
                      {Math.round(processingOverview.metrics.success_rate * 100)}%
                    </div>
                    <div className="text-sm text-gray-500">Success Rate</div>
                  </div>
                </div>
              </Card>

              {/* Active Projects */}
              {processingOverview.active_projects.length > 0 && (
                <Card className="p-6">
                  <h4 className="text-lg font-semibold mb-4">Active Projects</h4>
                  <div className="space-y-2">
                    {processingOverview.active_projects.map((project) => (
                      <div key={project.project_id} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                        <span className="font-medium">Project {project.project_id}</span>
                        <div className="flex items-center space-x-2">
                          {getPriorityBadge(project.priority)}
                          <Badge variant="default">{project.task_status}</Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>
              )}

              {/* Queue Statistics */}
              {Object.keys(processingOverview.queue_statistics).length > 0 && (
                <Card className="p-6">
                  <h4 className="text-lg font-semibold mb-4">Queue Statistics by Priority</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {Object.entries(processingOverview.queue_statistics).map(([priority, stats]) => (
                      <div key={priority} className="text-center p-3 bg-gray-50 rounded-lg">
                        <div className="text-lg font-bold">{stats.count}</div>
                        <div className="text-sm text-gray-500">{stats.label}</div>
                        <div className="text-xs text-gray-400">
                          Avg wait: {Math.round(stats.avg_wait_time)}s
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>
              )}
            </div>
          ) : (
            <Card className="p-6 text-center text-gray-500">
              Loading system overview...
            </Card>
          )}
        </div>
      )}
    </div>
  );
};

export default EnhancedAssetProcessor;
